import { useState } from 'react'
import { LANGUAGE_LIST, languageOf, urlForLanguage, type LanguageCode } from '../lib/languages'
import { startCheckout, startTrial } from '../lib/api'
import { useStore } from '../state/store'
import { Logo } from '../components/Logo'

export const PRICE_PER_MONTH = 19.95

/**
 * What you see on a language site you do not hold a subscription for.
 *
 * Deliberately not a dead end: it names the price, and it lists the languages
 * you *do* hold with links straight to them, so arriving here by mistake — a
 * bookmark, a shared link — costs one click rather than a puzzle.
 */
export function SubscribeView({
  language,
  held,
  trialAvailable = false,
}: {
  language: LanguageCode
  /** Languages this account already has, for the way out. */
  held: string[]
  /** Whether the free trial of this language is still on offer. */
  trialAvailable?: boolean
}) {
  const lang = languageOf(language)
  const { data } = useStore()
  const others = LANGUAGE_LIST.filter((l) => held.includes(l.code))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Hands off to Stripe's hosted checkout.
   *
   * A full-page navigation rather than a new tab: the return trip carries
   * ?billing=done and needs to land back in this app, and a popup would be
   * blocked about half the time anyway.
   */
  async function subscribe() {
    setBusy(true)
    setError(null)
    const result = await startCheckout(data.sync.token ?? '')
    if (!result.ok) {
      setBusy(false)
      setError(result.error)
      return
    }
    window.location.href = result.url
  }

  return (
    <div className="signin">
      <div className="signin-head">
        <Logo size={44} brand={lang.ui.brand} />
      </div>

      <h1 className="page-title" style={{ marginBottom: '0.3rem' }}>
        {lang.name} is not on your account
      </h1>
      <p className="lede" style={{ marginBottom: '1.4rem' }}>
        Each language is a separate subscription — ${PRICE_PER_MONTH} a month. Your islands, your
        practice history and your devices all stay exactly as they are; this only decides which
        languages you can open.
      </p>

      {error && (
        <div className="notice is-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.5rem' }}>
          Add {lang.name}
        </div>

        {trialAvailable ? (
          <>
            <p className="english" style={{ marginTop: 0 }}>
              Try it free for seven days. No card, nothing to cancel — it simply stops, and anything
              you build is kept.
            </p>
            <button
              type="button"
              className="btn is-primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                setError(null)
                const result = await startTrial(data.sync.token ?? '')
                setBusy(false)
                if (!result.ok) {
                  setError(result.error)
                  return
                }
                // The whole app changes shape once the language is unlocked, so
                // reload rather than trying to patch state in place.
                window.location.reload()
              }}
            >
              {busy ? 'Starting…' : `Start the free week of ${lang.name}`}
            </button>
            <p className="muted" style={{ fontSize: '0.84rem', margin: '0.6rem 0 0' }}>
              ${PRICE_PER_MONTH} a month afterwards, or{' '}
              <button type="button" className="btn is-small is-quiet" disabled={busy} onClick={subscribe}>
                subscribe now
              </button>{' '}
              and skip the reminder.
            </p>
          </>
        ) : (
          <>
            <p className="english" style={{ marginTop: 0 }}>
              ${PRICE_PER_MONTH} per month. Your islands in this language are kept exactly as they
              are and come straight back when it is added.
            </p>
            <button type="button" className="btn is-primary" disabled={busy} onClick={subscribe}>
              {busy ? 'Opening checkout…' : `Subscribe to ${lang.name}`}
            </button>
            <p className="muted" style={{ fontSize: '0.84rem', margin: '0.6rem 0 0' }}>
              Card handling is Stripe's — the card never touches this site. Cancel any time from the
              same account.
            </p>
          </>
        )}
      </div>

      {others.length > 0 && (
        <div className="card">
          <div className="stat-label" style={{ marginBottom: '0.5rem' }}>
            On your account
          </div>
          <div className="row">
            {others.map((other) => (
              <a
                key={other.code}
                className="btn"
                href={urlForLanguage(other.code, window.location.hostname)}
              >
                <span className="hanzi">{other.nativeName}</span>
                <span className="muted" style={{ fontSize: '0.82rem' }}>
                  {other.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {others.length === 0 && (
        <div className="empty">
          There are no languages on this account yet. An administrator can add one.
        </div>
      )}
    </div>
  )
}
